import { useCallback, useMemo, useState } from 'react';
import { useEffectReducer } from 'use-effect-reducer';
import { useTrackingChangeDatalayer } from '../../../../_integrations/everymundo/tracking/airlines';
import { filtersReducer } from '../../_commons/filtersReducer';
import { useSorting, transformSortingOption } from '../../_commons/sorting';
import { useOriginDestination } from '../../_commons/locations';
import { useRecommendation } from './useRecommendations';
import { useForm } from '../../NewsletterContainer/hook/filters';
import { getDateRange } from '../../_commons/dateRange';

const OriginDestinationLogic = {
  SAME: 'same',
  DIFFERENT: 'different'
};

const useFilters = ({
  promptSuggestion,
  filter,
  filter: { prePopulation, integrationSettings, meta, pageContext },
  getRecommendations,
  getFares,
  initialOrigins,
  initialDestinations,
  originDestinationLogic = OriginDestinationLogic.DIFFERENT,
  pagination,
  onLoadMore,
  credits,
  translate,
  fetchCredits
}) => {
  const initialState = useMemo(
    () => ({
      prompt: prePopulation.prompt || '',
      origin: { value: '' },
      destination: { value: '' },
      dateRange: getDateRange(prePopulation),
      sorting: transformSortingOption(prePopulation.sorting)
    }),
    [prePopulation]
  );

  const [memoryPrompt, setMemory] = useState([]);

  const track = useTrackingChangeDatalayer();
  const tracking = useCallback(
    (_, event) => {
      track(event.prevState, event.action, _);
    },
    [track]
  );

  const [state, dispatch] = useEffectReducer(filtersReducer, initialState, { tracking });
  const [fetchData, setFetchData] = useState(false);
  const [loadMoreClick, setLoadMoreClick] = useState(false);
  const [thumbActive, setThumbActive] = useState('');

  const trackingDispatch = useCallback(
    (event) => {
      return dispatch({ ...event, category: 'tracking ' });
    },
    [dispatch]
  );

  const locations = useOriginDestination({
    dispatch,
    origin: state.origin,
    destination: state.destination,
    showOrigin: true,
    showDestination: true,
    filter,
    initialOrigins,
    initialDestinations,
    originDestinationLogic,
    userInteraction: false,
    secondary: false
  });

  const refetch = useMemo(
    () => () => {
      if (locations.origin.isError) {
        locations.origin.refetch();
      }
      if (locations.destination.isError) {
        locations.destination.refetch();
      }
    },
    [locations]
  );
  const sorting = useSorting({
    dispatch,
    value: state.sorting,
    initValue: transformSortingOption(prePopulation.sorting)
  });

  const prompt = useForm({
    dispatch,
    input: 'prompt',
    value: state.prompt,
    initValue: '',
    lastValue: state.prompt,
    dispatchType: 'INSERT_PROMPT'
  });

  const fares = useRecommendation({
    prompt: state.prompt,
    dateRange: state.dateRange,
    sorting: sorting.value,
    locations,
    prePopulation,
    pagination,
    meta,
    integrationSettings,
    getFares,
    getRecommendations,
    fetchData,
    setFetchData,
    onLoadMore: (info) => {
      onLoadMore(info);
      trackingDispatch({ type: 'LOAD_MORE' });
    },
    setLoadMoreClick,
    pageContext,
    trackingDispatch,
    credits,
    fetchCredits
  });

  const promptTranslated = useMemo(() => {
    return promptSuggestion.map((option) => ({
      label: translate(option.label),
      value: translate(option.label),
      ...option
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translate, promptSuggestion]);

  const searchRecommendation = () => {
    setLoadMoreClick(false);
    setThumbActive('');

    //Adding a new element to memory

    const mergedOptions = [...memoryPrompt, ...promptTranslated];
    const isPromptInSuggestions = mergedOptions.some(
      (suggestion) => suggestion.value === prompt.value
    );

    if (!isPromptInSuggestions) {
      setMemory((prevMemory) => [
        ...prevMemory,
        {
          label: prompt.value,
          value: prompt.value,
          icon: 'history'
        }
      ]);
    }

    dispatch({
      type: 'SEARCH_PROMPT',
      payload: { prompt: state?.prompt }
    });
    setFetchData(true);
  };

  const isError =
    fares?.match('ERROR') || locations?.origin?.isError || locations?.destination?.isError;
  const disabledCTA = !prompt?.value || prompt?.value === '' ? true : false;

  const isPromptInvalid = fares?.match('PROMPT_INVALID');

  return {
    prompt: {
      ...prompt,
      disabled: credits?.value === 0,
      onKeyDown: (e) => {
        if (e.keyCode === 13 && !disabledCTA && credits?.value > 0) {
          searchRecommendation();
        }
      },
      memoryPrompt,
      onEnterKey: () => {
        if (!disabledCTA && credits?.value > 0) {
          searchRecommendation();
        }
      },
      searchRecommendation
    },
    likeActions: {
      like: {
        active: thumbActive === 'like',
        disabled: thumbActive === 'unlike',
        onClick: () => {
          if (thumbActive === '' && thumbActive !== 'like') {
            setThumbActive('like');
            dispatch({
              type: 'LIKE',
              payload: { prompt: state?.prompt }
            });
          }
        }
      },
      unlike: {
        active: thumbActive === 'unlike',
        disabled: thumbActive === 'like',
        onClick: () => {
          if (thumbActive === '' && thumbActive !== 'unlike') {
            setThumbActive('unlike');
            dispatch({
              type: 'UNLIKE',
              payload: { prompt: state?.prompt }
            });
          }
        }
      }
    },
    loadMoreClick,
    disabledCTA,
    isError,
    isPromptInvalid,
    sorting,
    refetch,
    locations,
    dispatch,
    state,
    isFetchData: fetchData,
    tracking: trackingDispatch,
    searchRecommendation,
    fares
  };
};

export { useFilters, OriginDestinationLogic };
